import React, { useCallback, useEffect, useMemo, useState } from 'react'
import {
  CloseOutlined,
  ExpandOutlined,
  FilePdfOutlined,
  LeftOutlined,
  RightOutlined,
} from '@ant-design/icons'
import { Input } from 'antd'
import LightboxViewer from '../LightboxViewer/LightboxViewer'
import {
  ComposerMediaPreview,
  ComposerPreviewCaption,
  ComposerPreviewMain,
  ComposerPreviewNavBtn,
  ComposerPreviewThumb,
  ComposerPreviewThumbs,
} from '../../NewsFeedStyled'

const MAX_CAPTION_LENGTH = 500

/**
 * Preview toàn bộ media trước khi đăng: carousel + fullscreen + caption từng ảnh.
 * Giữ nguyên thứ tự mediaFiles (không reorder).
 */
const ComposerMediaEditor = ({
  mediaFiles = [],
  onChangeCaption,
  onRemoveItem,
  onClearAll,
  disabled = false,
}) => {
  const [activeIndex, setActiveIndex] = useState(0)
  const [lightboxOpen, setLightboxOpen] = useState(false)

  useEffect(() => {
    if (mediaFiles.length === 0) {
      setActiveIndex(0)
      return
    }
    if (activeIndex >= mediaFiles.length) {
      setActiveIndex(mediaFiles.length - 1)
    }
  }, [mediaFiles.length, activeIndex])

  const current = mediaFiles[activeIndex] || null

  const goPrev = useCallback(
    e => {
      e?.stopPropagation?.()
      if (mediaFiles.length <= 1) return
      setActiveIndex(prev => (prev > 0 ? prev - 1 : mediaFiles.length - 1))
    },
    [mediaFiles.length]
  )

  const goNext = useCallback(
    e => {
      e?.stopPropagation?.()
      if (mediaFiles.length <= 1) return
      setActiveIndex(prev => (prev < mediaFiles.length - 1 ? prev + 1 : 0))
    },
    [mediaFiles.length]
  )

  const lightboxMedia = useMemo(
    () =>
      mediaFiles.map(m => ({
        id: m.id || m.fileId,
        mediaType: m.type,
        media_type: m.type,
        fileUrl: m.preview,
        file_url: m.preview,
        fileId: m.fileId,
        file_id: m.fileId,
        fileName: m.fileName || m.file?.name,
        file_name: m.fileName || m.file?.name,
        caption: m.caption || '',
      })),
    [mediaFiles]
  )

  if (!current || mediaFiles.length === 0) return null

  return (
    <>
      <ComposerMediaPreview>
        <ComposerPreviewMain
          onClick={() => {
            if (current.type === 'IMAGE' || current.type === 'VIDEO') {
              setLightboxOpen(true)
            }
          }}
          style={{
            cursor:
              current.type === 'IMAGE' || current.type === 'VIDEO'
                ? 'zoom-in'
                : 'default',
          }}>
          {current.type === 'IMAGE' && current.preview && (
            <img src={current.preview} alt={`preview-${activeIndex}`} />
          )}
          {current.type === 'VIDEO' && current.preview && (
            <video
              src={current.preview}
              controls
              onClick={e => e.stopPropagation()}
            />
          )}
          {(current.type === 'PDF' || current.type === 'DOCUMENT') && (
            <div
              style={{
                padding: 20,
                textAlign: 'center',
                color: '#050505',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 8,
              }}>
              <FilePdfOutlined style={{ fontSize: 48, color: '#ff4d4f' }} />
              <div style={{ wordBreak: 'break-all', fontSize: 13 }}>
                {current.fileName || current.file?.name || 'Tài liệu'}
              </div>
            </div>
          )}

          {mediaFiles.length > 1 && (
            <>
              <ComposerPreviewNavBtn className="prev" onClick={goPrev}>
                <LeftOutlined />
              </ComposerPreviewNavBtn>
              <ComposerPreviewNavBtn className="next" onClick={goNext}>
                <RightOutlined />
              </ComposerPreviewNavBtn>
            </>
          )}

          <div
            style={{
              position: 'absolute',
              top: 8,
              left: 8,
              background: 'rgba(0,0,0,0.55)',
              color: '#fff',
              borderRadius: 12,
              padding: '2px 10px',
              fontSize: 12,
              fontWeight: 600,
              zIndex: 3,
            }}>
            {activeIndex + 1} / {mediaFiles.length}
          </div>

          {(current.type === 'IMAGE' || current.type === 'VIDEO') && (
            <button
              type="button"
              title="Xem toàn màn hình"
              onClick={e => {
                e.stopPropagation()
                setLightboxOpen(true)
              }}
              style={{
                position: 'absolute',
                top: 8,
                right: 48,
                width: 36,
                height: 36,
                borderRadius: '50%',
                border: 'none',
                background: 'rgba(0,0,0,0.6)',
                color: '#fff',
                cursor: 'pointer',
                zIndex: 4,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}>
              <ExpandOutlined />
            </button>
          )}

          {onClearAll && (
            <div
              onClick={e => {
                e.stopPropagation()
                if (!disabled) onClearAll()
              }}
              title="Xóa tất cả"
              style={{
                position: 'absolute',
                top: 8,
                right: 8,
                width: 36,
                height: 36,
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                background: 'rgba(0,0,0,0.6)',
                color: '#fff',
                cursor: disabled ? 'not-allowed' : 'pointer',
                zIndex: 4,
              }}>
              <CloseOutlined />
            </div>
          )}
        </ComposerPreviewMain>

        {mediaFiles.length > 1 && (
          <ComposerPreviewThumbs>
            {mediaFiles.map((m, idx) => (
              <ComposerPreviewThumb
                key={m.id || m.fileId || idx}
                className={idx === activeIndex ? 'active' : ''}
                onClick={() => setActiveIndex(idx)}
                title={m.fileName || m.file?.name || `File ${idx + 1}`}>
                {m.type === 'IMAGE' && m.preview ? (
                  <img src={m.preview} alt="" />
                ) : m.type === 'VIDEO' && m.preview ? (
                  <video src={m.preview} muted />
                ) : (
                  <div
                    style={{
                      width: '100%',
                      height: '100%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      background: '#eee',
                      fontSize: 18,
                    }}>
                    <FilePdfOutlined style={{ color: '#ff4d4f' }} />
                  </div>
                )}
                {onRemoveItem && (
                  <button
                    type="button"
                    onClick={e => {
                      e.stopPropagation()
                      if (!disabled) onRemoveItem(m)
                    }}
                    style={{
                      position: 'absolute',
                      top: 2,
                      right: 2,
                      width: 18,
                      height: 18,
                      borderRadius: '50%',
                      border: 'none',
                      background: 'rgba(0,0,0,0.65)',
                      color: '#fff',
                      fontSize: 11,
                      lineHeight: '18px',
                      padding: 0,
                      cursor: 'pointer',
                    }}>
                    ×
                  </button>
                )}
              </ComposerPreviewThumb>
            ))}
          </ComposerPreviewThumbs>
        )}

        {(current.type === 'IMAGE' || current.type === 'VIDEO') && (
          <ComposerPreviewCaption>
            <div className="nf-caption-head">
              <div className="nf-caption-label">
                <span className="nf-caption-label-dot" />
                Mô tả ảnh {activeIndex + 1}
                {mediaFiles.length > 1 ? ` / ${mediaFiles.length}` : ''}
              </div>
              <div
                className={`nf-caption-meta${
                  (current.caption || '').length >= MAX_CAPTION_LENGTH * 0.9
                    ? ' warn'
                    : ''
                }`}>
                {(current.caption || '').length}/{MAX_CAPTION_LENGTH}
              </div>
            </div>
            <Input.TextArea
              value={current.caption || ''}
              onChange={e => {
                const value = e.target.value.slice(0, MAX_CAPTION_LENGTH)
                onChangeCaption?.(current, value)
              }}
              placeholder="Thêm mô tả cho ảnh này (tuỳ chọn)..."
              autoSize={{ minRows: 2, maxRows: 4 }}
              maxLength={MAX_CAPTION_LENGTH}
              disabled={disabled}
            />
          </ComposerPreviewCaption>
        )}

        <div
          style={{
            padding: '8px 14px',
            fontSize: 12,
            color: '#64748b',
            background: '#fff',
            borderTop: '1px solid #e2e8f0',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}>
          <span>{mediaFiles.length} file đính kèm</span>
          <span style={{ color: '#94a3b8' }}>
            Vuốt hoặc chọn thumbnail để đổi ảnh
          </span>
        </div>
      </ComposerMediaPreview>

      {lightboxOpen && (
        <LightboxViewer
          media={lightboxMedia}
          initialIndex={activeIndex}
          onClose={() => setLightboxOpen(false)}
        />
      )}
    </>
  )
}

export default ComposerMediaEditor
